import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { CalendarDays, MapPin, Award, Send, Clock, CreditCard, CheckCircle } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import { useAuth } from '../context/AuthContext';

const API_URL = "";

const PLACEMENT_STYLES = {
  '1st': { bg: 'bg-yellow-50 border-yellow-300', badge: 'bg-yellow-500 text-white', label: '1st Place' },
  '2nd': { bg: 'bg-stone-50 border-stone-300', badge: 'bg-stone-400 text-white', label: '2nd Place' },
  '3rd': { bg: 'bg-amber-50 border-amber-300', badge: 'bg-amber-700 text-white', label: '3rd Place' }
};

const CallToActionBanner = ({ openCall, mySubmission }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const deadline = new Date(openCall.deadline);
  const now = new Date();
  const daysLeft = Math.ceil((deadline - now) / (1000 * 60 * 60 * 24));
  const allowNonMember = openCall.allow_non_member_submissions !== false;

  const handleMemberSubmit = () => {
    if (mySubmission) {
      navigate(`/members/submissions/${openCall.id}?edit=${mySubmission.id}`);
    } else if (user) {
      navigate(`/members/submissions/${openCall.id}`);
    } else {
      navigate(`/members/login?redirect=/members/submissions/${openCall.id}`);
    }
  };

  return (
    <div className="bg-white border-2 border-stone-200 rounded-xl p-6 sm:p-8 mb-10 shadow-sm" data-testid="call-to-action-banner">
      <h3 className="text-xl font-serif text-stone-900 mb-1">Submit Your Work</h3>
      <p className="text-sm text-stone-500 flex items-center gap-1.5 mb-4">
        <Clock size={14} className="text-cafa-red flex-shrink-0" />
        {daysLeft > 0
          ? <span>Deadline in <strong className="text-stone-800">{daysLeft} day{daysLeft !== 1 ? 's' : ''}</strong> &mdash; {deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          : <span>Deadline: {deadline.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
        }
      </p>
      {openCall.instructions && (
        <p className="text-sm text-stone-500 mb-5 leading-relaxed">{openCall.instructions}</p>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        {mySubmission ? (
          mySubmission.payment_status === 'pending' ? (
            <Button onClick={handleMemberSubmit} className="bg-amber-600 hover:bg-amber-700 text-white" size="lg" data-testid="complete-payment-btn">
              <CreditCard size={16} className="mr-2" /> Complete Payment
            </Button>
          ) : (
            <Button onClick={handleMemberSubmit} variant="outline" className="border-green-400 text-green-700 hover:bg-green-50" size="lg" data-testid="continue-submission-btn">
              <CheckCircle size={16} className="mr-2" /> Submitted &mdash; View Submission
            </Button>
          )
        ) : (
          <>
            <Button onClick={handleMemberSubmit} className="bg-cafa-red hover:bg-red-700 text-white" size="lg" data-testid="detail-member-submit-btn">
              <Send size={16} className="mr-2" /> {allowNonMember ? 'Submit as Member' : 'Submit'}
            </Button>
            {allowNonMember && (
              <Button onClick={() => navigate(`/submissions/guest/${openCall.id}`)} variant="outline" className="border-stone-300 text-stone-700 hover:bg-stone-50" size="lg" data-testid="detail-guest-submit-btn">
                Submit as Guest
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export const ExhibitionDetail = () => {
  const { slug } = useParams();
  const { token } = useAuth();
  const [exhibition, setExhibition] = useState(null);
  const [winners, setWinners] = useState([]);
  const [mySubmission, setMySubmission] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const exRes = await fetch(`${API_URL}/api/exhibitions/public/${slug}`);
        if (!exRes.ok) return;
        const ex = await exRes.json();
        setExhibition(ex);

        const winRes = await fetch(`${API_URL}/api/judging/winners/${ex.id}`);
        if (winRes.ok) setWinners(await winRes.json());
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // Fetch user's existing submission for this exhibition's call
  useEffect(() => {
    if (!token || !exhibition?.open_call?.id) return;
    fetch(`${API_URL}/api/submissions/my`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : [])
      .then(subs => {
        const callId = exhibition.open_call.id;
        const sub = subs.find(s => s.call_id === callId);
        setMySubmission(sub || null);
      })
      .catch(() => {});
  }, [token, exhibition]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cafa-red"></div></div>;
  if (!exhibition) return (
    <div className="min-h-screen flex flex-col items-center justify-center text-stone-500">
      <SEOHead title="Exhibition Not Found" description="" noindex={true} />
      <h1 className="text-2xl font-serif text-stone-700 mb-2">Exhibition Not Found</h1>
      <p className="mb-6">The exhibition you're looking for doesn't exist or has been removed.</p>
      <a href="/exhibitions" className="text-cafa-red hover:underline">Browse All Exhibitions</a>
    </div>
  );

  // Group winners by category
  const winnersByCategory = {};
  for (const w of winners) {
    const cat = w.category_name || 'General';
    if (!winnersByCategory[cat]) winnersByCategory[cat] = [];
    winnersByCategory[cat].push(w);
  }

  return (
    <div className="min-h-screen bg-white" data-testid="exhibition-detail-page">
      <SEOHead title={exhibition.title} description={exhibition.summary} canonicalPath={`/exhibitions/${slug}`} />

      {/* Hero */}
      <div className="relative bg-stone-900 text-white">
        {exhibition.hero_image_url && (
          <img src={exhibition.hero_image_url} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        )}
        <div className="relative max-w-4xl mx-auto px-4 py-16 sm:py-24 text-center">
          <h1 className="text-3xl sm:text-5xl font-serif mb-4">{exhibition.title}</h1>
          {exhibition.summary && <p className="text-lg text-stone-300 mb-6">{exhibition.summary}</p>}
          <div className="flex flex-wrap justify-center gap-6 text-stone-300">
            <span className="flex items-center gap-2"><CalendarDays size={18} />
              {new Date(exhibition.start_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} – {new Date(exhibition.end_date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
            <span className="flex items-center gap-2"><MapPin size={18} /> {exhibition.location}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Call for Submissions CTA */}
        {exhibition.open_call && <CallToActionBanner openCall={exhibition.open_call} mySubmission={mySubmission} />}
        {exhibition.has_closed_call && !exhibition.open_call && (
          <div className="bg-stone-50 border border-stone-200 rounded-lg px-6 py-4 mb-10 text-sm text-stone-500 text-center" data-testid="submissions-closed-banner">
            Submissions for this exhibition are closed (deadline passed).
          </div>
        )}

        {/* Description */}
        {exhibition.description && (
          <div className="prose prose-stone max-w-none mb-12">
            <p className="text-lg leading-relaxed text-stone-700">{exhibition.description}</p>
          </div>
        )}

        {/* Winners */}
        {Object.keys(winnersByCategory).length > 0 && (
          <section className="mb-12" data-testid="winners-section">
            <div className="flex items-center gap-3 mb-8">
              <Award size={28} className="text-yellow-500" />
              <h2 className="text-2xl sm:text-3xl font-serif text-stone-900">Award Winners</h2>
            </div>

            {Object.entries(winnersByCategory).map(([catName, catWinners]) => (
              <div key={catName} className="mb-10">
                <h3 className="text-xl font-serif text-stone-800 mb-5 pb-2 border-b">{catName}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
                  {catWinners.sort((a,b) => a.placement.localeCompare(b.placement)).map(w => {
                    const style = PLACEMENT_STYLES[w.placement] || PLACEMENT_STYLES['3rd'];
                    return (
                      <Card key={w.id} className={`overflow-hidden border-2 ${style.bg}`} data-testid={`winner-${w.placement}-${catName}`}>
                        {w.artwork?.image_urls?.[0] && (
                          <div className="aspect-square">
                            <img src={w.artwork.image_urls[0]} alt={w.artwork?.title || ''} className="w-full h-full object-cover" />
                          </div>
                        )}
                        <CardContent className="p-4 text-center">
                          <Badge className={`${style.badge} mb-2`}>{style.label}</Badge>
                          <h4 className="font-semibold text-stone-900">{w.artwork?.title || ''}</h4>
                          <p className="text-sm text-stone-500 mt-1">{w.artwork?.medium || ''} &bull; {w.artwork?.size || ''}</p>
                          <p className="text-sm font-medium text-stone-700 mt-2">by {w.artist_name || 'Unknown'}</p>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </section>
        )}
      </div>
    </div>
  );
};

export default ExhibitionDetail;
